import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import EditorContext from "../../../../contexts/EditorContext";
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Snackbar, Alert, TextField, IconButton, Tooltip, useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AddSignature from "./AddSignature";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';

import {
  addSignatory, deleteSignatory, createSignatureRequest,
  getSignatureStatus, cancelSignatureRequest, sendSignatureReminder
} from "../../../../redux/actions/signatureAction";
import { fetchContract } from "../../../../redux/actions/contractAction";
import SigningDialog from "../../dialogs/SigningDialog";

const STATUS_CONFIG = {
  signed:    { color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> },
  pending:   { color: 'warning', icon: <HourglassEmptyIcon sx={{ fontSize: 13 }} /> },
  vide:      { color: 'default', icon: <HourglassEmptyIcon sx={{ fontSize: 13 }} /> },
  refused:   { color: 'error',   icon: <CancelOutlinedIcon sx={{ fontSize: 13 }} /> },
  expired:   { color: 'error',   icon: <CancelOutlinedIcon sx={{ fontSize: 13 }} /> },
  cancelled: { color: 'error',   icon: <CancelOutlinedIcon sx={{ fontSize: 13 }} /> },
};

const Signature = ({ contractId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const editorRef = useContext(EditorContext);
  const { loading, error, successMessage } = useSelector(state => state.signature || {});
  const contract = useSelector(state => state.contract.contract);
  const currentUser = useSelector(state => state.auth.user.user);

  const [collaborators, setCollaborators] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [unresolvedWarning, setUnresolvedWarning] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [reminderMessage, setReminderMessage] = useState(t("signature.reminderDialog.placeholder"));
  const [signingDialogOpen, setSigningDialogOpen] = useState(false);

  useEffect(() => { if (contractId) fetchSignatureStatus(); }, [contractId]);
  useEffect(() => { setReminderMessage(t("signature.reminderDialog.placeholder")); }, [i18n.language, t]);

  useEffect(() => {
    if (successMessage) { setAlertMessage(successMessage); setShowSuccessAlert(true); fetchSignatureStatus(); }
  }, [successMessage]);

  useEffect(() => {
    if (error) { setAlertMessage(error); setShowErrorAlert(true); }
  }, [error]);

  const fetchSignatureStatus = () => {
    if (!contractId) return;
    dispatch(getSignatureStatus(contractId))
      .then(response => {
        if (Array.isArray(response)) {
          setCollaborators(response.map(sig => ({
            email: sig.signer?.email || sig.signer_email || "Unknown",
            first_name: sig.signer?.first_name || sig.signer_first_name || "",
            last_name: sig.signer?.last_name || sig.signer_last_name || "",
            status: sig.status,
            signed_at: sig.signed_at
          })));
        }
      })
      .catch(() => { setAlertMessage(t("signature.alerts.error")); setShowErrorAlert(true); });
  };

  const handleAdd = (email, firstName, lastName) => {
    if (email && !collaborators.some(c => c.email === email)) {
      dispatch(addSignatory(contractId, email, firstName, lastName))
        .then(() => { ReactGA.event({ category: 'Signature', action: 'Add Signatory' }); tracker.event({ category: 'Signature', action: 'Add Signatory' }); })
        .catch(() => { setAlertMessage(t("signature.alerts.error")); setShowErrorAlert(true); });
    }
    setIsAdding(false);
  };

  const handleDelete = (email) => {
    dispatch(deleteSignatory(contractId, email))
      .then(() => { ReactGA.event({ category: 'Signature', action: 'Delete Signatory' }); tracker.event({ category: 'Signature', action: 'Delete Signatory' }); })
      .catch(() => { setAlertMessage(t("signature.alerts.error")); setShowErrorAlert(true); });
  };

  const confirmSendSignatures = () => {
    setShowConfirm(false);
    dispatch(createSignatureRequest(contractId, [], window.location.origin, 7, 3))
      .then(() => setTimeout(() => fetchSignatureStatus(), 1000))
      .catch(console.error);
  };

  const handleCancelSignature = () => {
    if (window.confirm(t("signature.alerts.confirmCancel"))) {
      dispatch(cancelSignatureRequest(contractId))
        .then(() => { fetchSignatureStatus(); setAlertMessage(t("signature.alerts.cancelSuccess")); setShowSuccessAlert(true); })
        .catch(() => { setAlertMessage(t("signature.alerts.error")); setShowErrorAlert(true); });
    }
  };

  const handleSendReminder = () => {
    setReminderDialogOpen(false);
    dispatch(sendSignatureReminder(contractId, reminderMessage))
      .then(() => {
        setAlertMessage(t("signature.alerts.reminderSent", { email: selectedEmail }));
        setShowSuccessAlert(true);
        ReactGA.event({ category: 'Signature', action: 'Send Reminder', label: `Reminder sent to ${selectedEmail}` });
        tracker.event({ category: 'Signature', action: 'Send Reminder', label: `Reminder sent to ${selectedEmail}` });
      })
      .catch(() => { setAlertMessage(t("signature.alerts.error")); setShowErrorAlert(true); });
  };

  const mapStatusText = (status) => {
    const map = { signed: "signature.statuses.signed", pending: "signature.statuses.pending", refused: "signature.statuses.refused", expired: "signature.statuses.expired", cancelled: "signature.statuses.cancelled", vide: "signature.statuses.notSent" };
    return t(map[status] || status);
  };

  const getStatusStyle = (status) => {
    const c = theme.palette;
    const map = {
      signed:    { bg: alpha(c.success.main, 0.1), color: c.success.main, border: alpha(c.success.main, 0.25) },
      pending:   { bg: alpha(c.warning.main, 0.1), color: c.warning.main, border: alpha(c.warning.main, 0.25) },
      vide:      { bg: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04), color: c.text.secondary, border: alpha(c.divider, 0.5) },
      refused:   { bg: alpha(c.error.main, 0.1),   color: c.error.main,   border: alpha(c.error.main, 0.25) },
      expired:   { bg: alpha(c.error.main, 0.07),  color: c.text.disabled,border: alpha(c.divider, 0.5) },
      cancelled: { bg: alpha(c.error.main, 0.07),  color: c.error.main,   border: alpha(c.error.main, 0.2) },
    };
    return map[status] || map.vide;
  };

  const canSend = () => {
    if (contract?.status === "PENDING_SIGNATURE") return false;
    return collaborators.some(c => c.status === "vide");
  };

  if (isAdding) {
    return <AddSignature onAdd={handleAdd} onCancel={() => setIsAdding(false)} existingEmails={collaborators.map(c => c.email)} />;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
          {t("signature.title")}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, mt: 0.25 }}>
          {t("signature.subtitle")}
        </Typography>
      </Box>

      {/* ── Signatories list ── */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto', mb: 1.5,
        display: 'flex', flexDirection: 'column', gap: 0.75,
        '&::-webkit-scrollbar': { width: '3px' },
        '&::-webkit-scrollbar-thumb': { background: alpha(theme.palette.text.secondary, 0.18), borderRadius: '4px' },
      }}>
        {collaborators.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            py: 5, gap: 1.5,
            border: `1.5px dashed ${alpha(theme.palette.divider, 0.6)}`,
            borderRadius: '12px',
          }}>
            <DrawOutlinedIcon sx={{ fontSize: 28, color: theme.palette.text.disabled }} />
            <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary }}>
              {t("signature.signatoryPlaceholder")}
            </Typography>
          </Box>
        ) : (
          collaborators.map(({ email, first_name, last_name, status, signed_at }) => {
            const st = getStatusStyle(status);
            const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.vide;
            return (
              <Box
                key={email}
                sx={{
                  p: 1.25, borderRadius: '10px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  backgroundColor: isDark ? alpha('#fff', 0.02) : '#fff',
                  transition: 'background-color 0.15s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: theme.palette.text.primary }}>
                      {first_name} {last_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {email}
                    </Typography>
                    {signed_at && (
                      <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.disabled, mt: 0.25 }}>
                        {t("signature.signedOn")} {new Date(signed_at).toLocaleDateString(i18n.language)}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {/* Status badge */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      px: 1, py: 0.35, borderRadius: '6px',
                      backgroundColor: st.bg, color: st.color,
                      border: `1px solid ${st.border}`,
                      fontSize: '0.7rem', fontWeight: 600,
                    }}>
                      {cfg.icon}
                      {mapStatusText(status)}
                    </Box>

                    {status === "pending" && currentUser?.email === email && (
                      <Tooltip title={t("signature.signNow")}>
                        <IconButton
                          size="small"
                          onClick={() => setSigningDialogOpen(true)}
                          sx={{
                            width: 26, height: 26, borderRadius: '7px',
                            color: theme.palette.success.main,
                            backgroundColor: alpha(theme.palette.success.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                            '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.16) },
                          }}
                        >
                          <DrawOutlinedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {status === "pending" && (
                      <Tooltip title={t("signature.sendReminder", "Envoyer un rappel")}>
                        <IconButton
                          size="small"
                          onClick={() => { setSelectedEmail(email); setReminderDialogOpen(true); }}
                          sx={{
                            width: 26, height: 26, borderRadius: '7px',
                            color: theme.palette.warning.main,
                            backgroundColor: alpha(theme.palette.warning.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                            '&:hover': { backgroundColor: alpha(theme.palette.warning.main, 0.16) },
                          }}
                        >
                          <NotificationsOutlinedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {status === "vide" && (
                      <Tooltip title={t("remove", "Retirer")}>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(email)}
                          sx={{
                            width: 26, height: 26, borderRadius: '7px',
                            color: alpha(theme.palette.text.secondary, 0.6),
                            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                            '&:hover': { color: theme.palette.error.main, borderColor: alpha(theme.palette.error.main, 0.4), backgroundColor: alpha(theme.palette.error.main, 0.06) },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
      </Box>

      {/* ── Action buttons ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`, pt: 1.5 }}>
        {/* Add signatory */}
        <Box
          component="button"
          onClick={() => setIsAdding(true)}
          disabled={contract?.status === "PENDING_SIGNATURE"}
          sx={{
            width: '100%', height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 1, borderRadius: '9px', cursor: contract?.status === "PENDING_SIGNATURE" ? 'not-allowed' : 'pointer',
            fontFamily: theme.typography.fontFamily, fontSize: '0.855rem', fontWeight: 500,
            backgroundColor: 'transparent', color: contract?.status === "PENDING_SIGNATURE" ? alpha(theme.palette.text.secondary, 0.5) : theme.palette.text.primary,
            border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            outline: 'none', transition: 'all 0.15s',
            '&:hover:not(:disabled)': { borderColor: alpha(theme.palette.text.secondary, 0.4), backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03) },
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
          }}
        >
          <AddOutlinedIcon sx={{ fontSize: 16 }} />
          <span>{t("signature.addButton")}</span>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {/* Send for signature */}
          <Box
            component="button"
            onClick={() => {
              const status = editorRef?.current?.getUnresolvedStatus?.();
              if (status && (status.unresolvedComments > 0 || status.pendingSuggestions > 0)) {
                setUnresolvedWarning(status);
              } else {
                setShowConfirm(true);
              }
            }}
            disabled={loading || !canSend()}
            sx={{
              flex: 1, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 1, borderRadius: '9px', cursor: canSend() ? 'pointer' : 'not-allowed',
              fontFamily: theme.typography.fontFamily, fontSize: '0.855rem', fontWeight: 600,
              background: canSend() ? (isDark ? alpha('#fff', 0.9) : alpha('#000', 0.87)) : (isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08)),
              color: canSend() ? (isDark ? '#000' : '#fff') : alpha(theme.palette.text.secondary, 0.5),
              border: 'none', outline: 'none', transition: 'opacity 0.15s',
              '&:hover:not(:disabled)': { opacity: 0.85 },
              '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
            }}
          >
            <SendOutlinedIcon sx={{ fontSize: 15 }} />
            <span>{t("signature.sendButton")}</span>
          </Box>

          {/* Cancel */}
          {collaborators.some(c => c.status === "pending") && (
            <Box
              component="button"
              onClick={handleCancelSignature}
              disabled={loading}
              sx={{
                height: 38, px: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '9px', cursor: 'pointer',
                fontFamily: theme.typography.fontFamily, fontSize: '0.78rem', fontWeight: 500,
                backgroundColor: 'transparent', color: theme.palette.error.main,
                border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
                outline: 'none', transition: 'all 0.15s',
                '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.06) },
                '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
              }}
            >
              {t("signature.cancelButton")}
            </Box>
          )}
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} PaperProps={{ sx: { borderRadius: '14px', maxWidth: 400, direction: isRTL ? 'rtl' : 'ltr' } }}>
        <DialogTitle sx={{ fontWeight: 700, pt: 3, fontSize: '1rem' }}>{t("signature.confirmationDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem' }}>{t("signature.confirmationDialog.message")}</Typography>
          <Typography variant="body2" sx={{ mt: 1.5, color: theme.palette.text.secondary, fontSize: '0.8rem' }}>{t("signature.confirmationDialog.details")}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setShowConfirm(false)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, borderRadius: '8px' }}>{t("signature.cancelButton")}</Button>
          <Button
            onClick={() => { ReactGA.event({ category: 'Signature', action: 'Send for Signature' }); tracker.event({ category: 'Signature', action: 'Send for Signature' }); confirmSendSignatures(); }}
            variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}
          >
            {t("signature.confirmButton")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unresolved Warning Dialog */}
      <Dialog open={!!unresolvedWarning} onClose={() => setUnresolvedWarning(null)} PaperProps={{ sx: { borderRadius: '14px', maxWidth: 400, direction: isRTL ? 'rtl' : 'ltr' } }}>
        <DialogTitle sx={{ fontWeight: 700, pt: 3, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberOutlinedIcon sx={{ color: theme.palette.warning.main, fontSize: 22 }} />
          {t("signature.unresolvedWarning.title")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', mb: 1.5 }}>{t("signature.unresolvedWarning.message")}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {unresolvedWarning?.unresolvedComments > 0 && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
                borderRadius: '8px', backgroundColor: alpha(theme.palette.warning.main, 0.08),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                  {t("signature.unresolvedWarning.commentsRemaining", { count: unresolvedWarning.unresolvedComments })}
                </Typography>
              </Box>
            )}
            {unresolvedWarning?.pendingSuggestions > 0 && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
                borderRadius: '8px', backgroundColor: alpha(theme.palette.warning.main, 0.08),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                  {t("signature.unresolvedWarning.suggestionsRemaining", { count: unresolvedWarning.pendingSuggestions })}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setUnresolvedWarning(null)} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}>
            {t("signature.unresolvedWarning.closeButton")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} PaperProps={{ sx: { borderRadius: '14px', maxWidth: 400, direction: isRTL ? 'rtl' : 'ltr' } }}>
        <DialogTitle sx={{ fontWeight: 700, pt: 3, fontSize: '1rem' }}>{t("signature.reminderDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5, fontSize: '0.875rem' }}>
            {t("signature.reminderDialog.message")} <strong>{selectedEmail}</strong>
          </Typography>
          <TextField
            fullWidth multiline rows={4} variant="outlined"
            value={reminderMessage}
            onChange={(e) => setReminderMessage(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: '10px', fontSize: '0.875rem' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setReminderDialogOpen(false)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, borderRadius: '8px' }}>{t("signature.cancelButton")}</Button>
          <Button onClick={handleSendReminder} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}>{t("signature.sendButton")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={showSuccessAlert} autoHideDuration={6000} onClose={() => setShowSuccessAlert(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setShowSuccessAlert(false)} severity="success" sx={{ borderRadius: '10px' }}>{alertMessage}</Alert>
      </Snackbar>
      <Snackbar open={showErrorAlert} autoHideDuration={6000} onClose={() => setShowErrorAlert(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setShowErrorAlert(false)} severity="error" sx={{ borderRadius: '10px' }}>{alertMessage}</Alert>
      </Snackbar>

      <SigningDialog
        open={signingDialogOpen}
        onClose={() => setSigningDialogOpen(false)}
        contractId={contractId}
        contractTitle={contract?.title}
        onSigningComplete={() => {
          setSigningDialogOpen(false);
          fetchSignatureStatus();
          dispatch(fetchContract(contractId));
        }}
      />
    </Box>
  );
};

export default Signature;
